/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// THE DASHBOARD'S WRITES BY ROLE (v1_826). The server refuses a write a role
// may not make; this gate stops the dashboard from sending it in the first
// place, so a read only member opening a case (which runs background writes:
// the seen stamp, record healing, restored screening results) sees nothing on
// screen, no alert and no error. A skipped write resolves as if it succeeded
// and is logged to the console only.
//
//   MLRO, or the platform owner: every write.
//   staff: add case notes, invite, resend an invitation, assign a case.
//   Phase 2: a case note is insert only for every role (the server and the
//   database refuse an edit or a delete, the MLRO's included).
//   read only: no write.
//
// Reads (selects, signed URLs, read routes) are never gated.

export const STAFF_SAVE_REPORT_FIELDS = Object.freeze(['assigned_to', 'assigned_to_email', 'assigned_at']);
// v1_833b: firm-question-answer (Record answer) is staff's too.
export const STAFF_ROUTES = Object.freeze(['invite', 'resend-invite', 'firm-question-answer']);
// Tables staff write directly, and the operations allowed on each.
export const STAFF_TABLES = Object.freeze({ case_notes: Object.freeze(['insert']) });

// Routes that write to a case, the firm or a client. Anything not listed is a
// read (document URLs, translations, reports, the assistant) and passes.
export const WRITE_ROUTES = Object.freeze([
  'adjust-tier', 'adversarial-review', 'analyze-document', 'attestation-manage', 'auto-actions', 'banking-invite',
  'calendar-reminder', 'case-decision', 'case-escalation', 'chainalysis', 'compliance-score', 'cross-reference', 'decision-snapshot',
  'decline-notification', 'delegate-item', 'delete-session', 'df-process', 'director-invite', 'external-share',
  'external-verify', 'firm-question-answer', 'generate-client-docs', 'generate-vqf-customer-profile', 'generate-vqf-form', 'generate-vqf-risk-profile',
  'interview-inconsistencies', 'invite', 'migrate-client', 'notify', 'notify-sumsub', 'notify-sumsub-form', 'pin-entity-type', 'pin-legal-form',
  'proportionality', 'proportionality-review', 'refresh-intel', 'registry', 'request-docs', 'request-info',
  'request-payment-confirmation', 'resend-invite', 'review-update', 'risk-assess', 'save-document', 'save-report',
  'screen-intermediaries', 'screen-parties', 'screening-resolve', 'sow-invite', 'sumsub', 'sumsub-aml', 'travel-rule',
  'update-session', 'welcome-approved',
]);

let state = { role: null, isAdmin: false };
export function setOfficerRole(role, isAdmin) { state = { role: role ? String(role).toLowerCase() : null, isAdmin: !!isAdmin }; }
export function officerRole() { return state.isAdmin ? 'mlro' : state.role; }

const skipLog = [];
export function skippedWrites() { return skipLog.slice(); }
function skip(what) {
  skipLog.push(what);
  try { console.debug('[write gate] skipped for role ' + officerRole() + ': ' + what); } catch (_) {}
}

// null role: not yet known. The dashboard loads the firm (and so the role)
// before any case can be opened, so nothing case related runs in that window.
export function mayWriteTable(table, op) {
  const r = officerRole();
  if (r === null || r === 'mlro') return true;
  if (r === 'staff') return !!(STAFF_TABLES[table] && STAFF_TABLES[table].includes(op));
  return false;
}
export function mayCallRoute(route, body) {
  const r = officerRole();
  if (r === null || r === 'mlro') return true;
  if (!WRITE_ROUTES.includes(route)) return true;
  if (r !== 'staff') return false;
  if (STAFF_ROUTES.includes(route)) return true;
  if (route === 'save-report') {
    let b = body;
    if (typeof b === 'string') { try { b = JSON.parse(b); } catch (_) { b = null; } }
    const keys = Object.keys((b && b.updates) || {});
    return keys.length > 0 && keys.every((k) => STAFF_SAVE_REPORT_FIELDS.includes(k));
  }
  return false;
}

// A query builder that swallows every call and resolves as a success.
function skippedQuery() {
  const result = { data: null, error: null, count: null, status: 204, statusText: 'Skipped', skipped: true };
  const p = Promise.resolve(result);
  const q = new Proxy(function () {}, {
    get(_, prop) {
      if (prop === 'then') return p.then.bind(p);
      if (prop === 'catch') return p.catch.bind(p);
      if (prop === 'finally') return p.finally.bind(p);
      return q;
    },
    apply() { return q; },
  });
  return q;
}

const WRITE_OPS = ['insert', 'update', 'upsert', 'delete'];

// PHASE 2: the server writes. A table write the gate allows is not sent to the
// database from the browser: it is captured (table, operation, values, id filter,
// whether it asks for the row back) and sent to /api/officer-write, which checks
// the caller's role in the row's firm and runs it with the firm pinned. The
// dashboard's five chain shapes are supported: insert, insert.select.single,
// update.eq, update.eq.select, delete.eq. Anything else is refused out loud
// (an error result), never sent half built and never silently dropped.
const ROUTED_METHODS = ['eq', 'select', 'single'];
export function routedWrite(client, table, op, args, sender = null) {
  const spec = { table, op, values: op === 'delete' ? null : (args[0] ?? null), filters: [], select: false, single: false };
  let unsupported = op === 'upsert' ? 'upsert' : (op !== 'delete' && args.length > 1 && args[1] ? 'options' : null);
  let promise = null;
  const send = sender || (async (body) => {
    const got = await client.auth.getSession();
    const session = got && got.data && got.data.session;
    const r = await (typeof window !== 'undefined' ? window.fetch : fetch)('/api/officer-write', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + ((session && session.access_token) || '') },
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    return { data: j.data ?? null, error: j.error || (r.ok ? null : { message: 'write failed ' + r.status }), status: r.status };
  });
  const run = () => promise || (promise = unsupported
    ? Promise.resolve({ data: null, error: { message: `The dashboard write ${op} on ${table} uses ${unsupported}, which the server write route does not take` }, status: 400 })
    : send(spec).catch(e => ({ data: null, error: { message: (e && e.message) || String(e) }, status: 0 })));
  const q = new Proxy(function () {}, {
    get(_, prop) {
      if (prop === 'then') return (a, b) => run().then(a, b);
      if (prop === 'catch') return (b) => run().catch(b);
      if (prop === 'finally') return (f) => run().finally(f);
      if (prop === 'eq') return (col, val) => { spec.filters.push([col, val]); return q; };
      if (prop === 'select') return () => { spec.select = true; return q; };
      if (prop === 'single') return () => { spec.single = true; return q; };
      return () => { if (!ROUTED_METHODS.includes(String(prop))) unsupported = unsupported || String(prop); return q; };
    },
  });
  return q;
}

export function guardSupabase(client) {
  return new Proxy(client, {
    get(target, prop) {
      if (prop === 'from') {
        return (table) => {
          const builder = target.from(table);
          return new Proxy(builder, {
            get(b, op) {
              if (WRITE_OPS.includes(op) && !mayWriteTable(table, op)) return () => { skip(`${op} ${table}`); return skippedQuery(); };
              if (WRITE_OPS.includes(op)) return (...args) => routedWrite(target, table, op, args);
              const v = b[op];
              return typeof v === 'function' ? v.bind(b) : v;
            },
          });
        };
      }
      if (prop === 'storage') {
        const storage = target.storage;
        return new Proxy(storage, {
          get(s, sp) {
            if (sp !== 'from') { const v = s[sp]; return typeof v === 'function' ? v.bind(s) : v; }
            return (bucket) => {
              const api = s.from(bucket);
              return new Proxy(api, {
                get(a, op) {
                  if (['upload', 'update', 'remove', 'move', 'copy'].includes(op) && officerRole() !== null && officerRole() !== 'mlro') return () => { skip(`storage ${op} ${bucket}`); return Promise.resolve({ data: null, error: null, skipped: true }); };
                  // PHASE 2: a direct browser upload is refused out loud; uploads go
                  // through officerUpload() below (the server chooses the path).
                  if (op === 'upload') return () => Promise.resolve({ data: null, error: { message: 'Direct uploads are closed: use officerUpload' } });
                  // v1_902: a case document's file is removed by the server with its
                  // record (officer-write, session_documents delete). A browser remove
                  // on that bucket found no delete rule and removed nothing, silently.
                  if (op === 'remove' && bucket === 'onboarding-documents') return () => Promise.resolve({ data: null, error: { message: 'Case document files are removed by the server with their record' } });
                  const v = a[op];
                  return typeof v === 'function' ? v.bind(a) : v;
                },
              });
            };
          },
        });
      }
      const v = target[prop];
      return typeof v === 'function' ? v.bind(target) : v;
    },
  });
}

export function routeOf(url) {
  const m = String(url || '').match(/^(?:https?:\/\/[^/]+)?\/api\/([a-z0-9-]+)/i);
  return m ? m[1].toLowerCase() : null;
}
export function guardFetch(baseFetch) {
  return function gatedFetch(input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const route = routeOf(url);
    const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (route && method !== 'GET' && !mayCallRoute(route, init && init.body)) {
      skip(`${method} /api/${route}`);
      return Promise.resolve(new Response(JSON.stringify({ success: true, skipped: true }), { status: 200, headers: { 'content-type': 'application/json' } }));
    }
    return baseFetch(input, init);
  };
}

// PHASE 2, STORAGE: an officer upload. The server chooses the path (inside the
// case, referral or firm the caller is an MLRO of) and returns a one time signed
// upload URL; the file goes straight to storage. Returns { path, error }.
export async function officerUpload(client, { purpose, sessionId = null, referralId = null, firmId = null, file }) {
  try {
    const got = await client.auth.getSession();
    const session = got && got.data && got.data.session;
    const r = await (typeof window !== 'undefined' ? window.fetch : fetch)('/api/officer-upload', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + ((session && session.access_token) || '') },
      body: JSON.stringify({ purpose, sessionId, referralId, firmId, fileName: (file && file.name) || 'document' }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.path || !j.token) return { path: null, error: { message: j.error || ('upload refused ' + r.status) } };
    const { error } = await client.storage.from(j.bucket).uploadToSignedUrl(j.path, j.token, file, { contentType: (file && file.type) || 'application/octet-stream' });
    return error ? { path: null, error } : { path: j.path, error: null };
  } catch (e) { return { path: null, error: { message: (e && e.message) || String(e) } }; }
}
